import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { LoadingBarService } from "@ngx-loading-bar/core";
import { ToastrService } from "ngx-toastr";
import * as XLSX from "xlsx";
import { ClassificationField } from "../../../models/classification-field";
import { ClassificationType } from "../../../models/classification-type";
import { IdValue } from "../../../models/id-value";
import { Registration } from "../../../models/registration";
import { ClassificationControllerService } from "../../../services/classifications-controller/classification-controller.service";
import { RegistrationControllerService } from "../../../services/registration-controller/registration-controller.service";
import { ServiceControllerService } from "../../../services/service-controller/service-controller.service";
import EXCEL_MIME_TYPES from "../../utils/excel-mime-types";
import { BaseError } from "../base-error";

@Component({
    selector: "classification-registration",
    templateUrl: "./classification-registration.component.html",
    providers: [ToastrService],
})

export class ClassificationRegistrationComponent extends BaseError implements OnInit {
    public services: IdValue[];
    public registrations: Registration[] = [];
    // private groups: ServiceRegistrationGroup[] = [];
    public serviceId: string = "";
    public key = "";
    public value = "";
    public userFilter: any = { name: "" };
    public showWarningModal = false;
    public selectedForDeleteItem: any;
    public selectedClassificationValue: string;
    public classifications: any[];
    public classificationFields: ClassificationField[] = [];
    public classificationTypeId: string;
    public selectedClassificationType: ClassificationType;
    public classificationTypes: ClassificationType[];

    public isLoading: boolean = false;

    constructor(
        private serviceControllerService: ServiceControllerService,
        private registrationController: RegistrationControllerService,
        private classificationControllerService: ClassificationControllerService,
        private route: ActivatedRoute,
        toastr: ToastrService,
        private loadingBar: LoadingBarService) {
        super(toastr);
    }

    public ngOnInit() {
        this.loadServices();
        this.route.params.subscribe((params) => {
          this.serviceId = params.serviceId;
          this.loadClassificationTypes(this.serviceId);
        });
    }

    public dropped(event: CdkDragDrop<Registration[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

            // component logic
            this.saveRegistrations();
        }
    }

    public loadServices() {
        this.serviceControllerService.getServicesList().subscribe(
            (data: any) => this.buildServices(data),
            (error) => this.showErrorMessageWithTitle("Error with service list loading: ", error),
        );
    }

    public loadRegistrations(serviceId: string) {
        if (serviceId) {
            this.registrationController.getRegistrationsList(serviceId).subscribe(
                (data) => this.registrations = data,
                (error) => this.showErrorMessageWithTitle("Error with service translations loading: ", error),
            );
        }
    }

    public loadClassificationTypes(serviceId: string) {
        this.classificationControllerService.getClassificationTypes().subscribe(
            (data) => this.buildDropDown(data),
            (error) => this.showErrorMessage(error),
        );
    }

    public buildDropDown(classifications: ClassificationType[]) {
        this.classificationTypes = classifications;
        this.classifications = [];
        for (const classification of classifications) {
            const idValue = new IdValue();
            idValue.id = classification.id;
            idValue.text = classification.name;
            this.classifications.push(idValue);
        }
        if (!this.selectedClassificationValue) {
            this.selected(this.classifications.find((o) => o.id === this.selectedClassificationValue));
        }
    }

    public selected(idValue: IdValue): void {
        if (idValue) {
            this.selectedClassificationValue = idValue.id;
            this.classificationTypeId = idValue.id;
            this.selectedClassificationType = this.classificationTypes.find((o) => o.id === idValue.id);
            // this.classificationTypesWithoutCurrent = this.classificationTypes.filter((classificationType) => {
            //    return classificationType !== this.selectedClassificationType;
            // });
            // this.classificationFields = [];
            // this.classificationControllerService.getClassificationsByType(idValue.id).subscribe(
            //     (data) => this.classificationFields = data,
            //     (error) => this.showErrorMessage(error),
            // );
        }
    }

    public serviceSelected(serviceId: any) {
        this.serviceId = serviceId;
        // this.loadGroups();
        this.loadRegistrations(this.serviceId);
        this.loadClassificationTypes(this.serviceId);
    }

    public buildServices(services: any[]) {
        this.services = [];
        for (const service of services) {
            const idValue = new IdValue();
            idValue.id = service.id;
            idValue.text = service.name;
            this.services.push(idValue);
        }
    }

    public addNewRegistration() {
        const registration = new Registration();
        registration.key = this.key;
        registration.name = this.value;
        this.registrations.push(registration);
        this.saveRegistrations();
    }

    public removeRegistration(isConfirmed, id: string) {
        if (isConfirmed) {
            this.deleteRegistration(id);
            // const newList: Registration[] = [];
            // for (const registration of this.registrations) {
            //     if (registration.id !== id) {
            //         newList.push(registration);
            //     }
            // }
            // this.registrations = newList;
            // this.saveRegistrations();
        }
    }

    public generateRegistrationsAndDeterminants() {
        if (this.selectedClassificationType) {
            this.registrationController.saveRegistrationsAndDeterminants(this.serviceId, this.selectedClassificationType.id).subscribe(
                (data) => {
                    this.registrations = data;
                    this.key = "";
                    this.value = "";
                    this.responseSuccessful("Data is saved!");
                },
                (error) => this.showErrorMessage(error),
                () => this.isLoading = false,
            );
        }
    }

    public saveRegistrations() {
        if (!this.isLoading) {
            this.isLoading = true;
            let customOrderIndex = 0;
            this.registrations.forEach((f) => {
                f.sortOrder = customOrderIndex;
                customOrderIndex++;
            });
            if (this.selectedClassificationType) {
                this.generateRegistrationsAndDeterminants();
            } else {
                this.registrationController.saveRegistrations(this.serviceId, this.registrations).subscribe(
                    (data) => {
                        this.registrations = data;
                        this.key = "";
                        this.value = "";
                        this.responseSuccessful("Data is saved!");
                    },
                    (error) => this.showErrorMessage(error),
                    () => this.isLoading = false,
                );
            }
        }
    }

    public deleteRegistration(registrationId: string) {
        this.registrationController.deleteRegistration(registrationId).subscribe(
            (data) => {
                this.responseSuccessful("Registration is deleted!");
                this.loadRegistrations(this.serviceId);
            },
            (error) => this.showErrorMessage(error),
        );
    }

    public onFileChange(evt: any) {
        this.loadingBar.start();
        /* wire up file reader */
        const target: DataTransfer = (evt.target) as DataTransfer;
        const onError = (e) => {
            this.loadingBar.complete();
            evt.target.value = ""; // clear input
            this.showErrorMessage(e);
        };
        try {
            if (target.files.length !== 1) {
                throw new Error("Cannot use multiple files");
            }
            if (!EXCEL_MIME_TYPES.some((excelMime) => {
                return target.files[0].type === excelMime;
            })) {
                throw new Error("Invalid file format");
            }
            const reader: FileReader = new FileReader();
            reader.onload = (e: any) => {
                try {
                    /* read workbook */
                    const bstr: string = e.target.result;
                    const wb: XLSX.WorkBook = XLSX.read(bstr, { type: "binary" });

                    /* grab first sheet */
                    const wsname: string = wb.SheetNames[0];
                    const ws: XLSX.WorkSheet = wb.Sheets[wsname];

                    /* save data */
                    const xlsxData = (XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false })); // Array of Arrays
                    /*
                      rows must keep structure of model, each row is Array:
                      0 - value
                      ignore childClassificationType type for now
                      1 - trans one
                      2 - trans two
                      ...

                    */
                    xlsxData.forEach((row: any) => {
                        if (!row[0]) {
                            return;
                        }
                        // Check if we have that value already
                        if (this.registrations.some((field) => {
                            return field.key === row[0];
                        })) {
                            return;
                        }
                        const registration = new Registration();
                        registration.key = row[0];
                        registration.name = row[1];
                        this.registrations.push(registration);
                    });
                    this.loadingBar.complete();
                    this.saveRegistrations();
                    evt.target.value = ""; // clear input
                } catch (err) {
                    onError(err);
                }
            };
            reader.readAsBinaryString(target.files[0]);
        } catch (e) {
            onError(e);
        }

    }

    public searchTextChanged(value: any) {
        const filter = {name: value};
        this.userFilter = filter;
    }

    public trackByFn(index, item) {
        return item && item.id; // or item.id
    }
}
